import type { ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router';
import { useIsEmberRoute } from '@repo/poly-hooks/hooks/use-is-ember-route.tsx';
import { browserHistory, ReactRouter } from './history';
import { NotFound } from './routes/not-found';
import { AppLayout } from './routes/app-layout';
import { BusinessActivity } from './routes/business-activity';
import { OwnerInfoRoute } from './routes/registration/owner-info/owner-info';
import { KycGuard, KycLayout, KycRoute } from './routes/registration/kyc';
import { UUID_REGEX } from 'register-partners-js/utils/uuid';
import { CompanyActivity } from './routes/company-activity';

function CheckIfEmberHasRoute(): React.ReactNode {
  const isEmberRoute = useIsEmberRoute();
  const { pathname } = useLocation();

  if (!isEmberRoute()) return <Navigate replace to="/404" />;

  // Ember's :redirection_id dynamic segment takes priority over /*path for
  // single-segment URLs, so emberRouter.recognize('/organizations') returns
  // 'welcome' even after replaceWith('react', ...). Guard against non-UUID
  // single-segment paths that Ember falsely claims — they are React-owned
  // routes that should fall through to 404 rather than render nothing.
  const [, segment, ...rest] = pathname.split('/');
  if (rest.length === 0 && segment && !UUID_REGEX.test(segment)) {
    return <Navigate replace to="/404" />;
  }

  return null;
}

export function Router({ path }: { path: string }): ReactNode {
  return (
    // @ts-expect-error react-router's History type has extra methods
    <ReactRouter history={browserHistory} key={path}>
      <Routes>
        <Route element={<AppLayout />}>
          <Route path="registrations/:registration_id">
            <Route element={<OwnerInfoRoute />} path="owner-info" />
            <Route element={<KycLayout />} path="new-kyc/*">
              <Route element={<KycGuard />}>
                <Route element={<KycRoute />} index />
              </Route>
            </Route>
          </Route>
          <Route element={<NotFound />} path="/404" />
          <Route
            element={<BusinessActivity />}
            path="/registrations/:registrationId/business-activity"
          />
          <Route
            element={<CompanyActivity />}
            path="/registrations/:registrationId/company-activity"
          />
          <Route element={<CheckIfEmberHasRoute />} path="*" />
        </Route>
      </Routes>
    </ReactRouter>
  );
}
